import { useSessionKey } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import {
  Button,
  EmptyNote,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useOpenTasks } from "@tasks/tasksQueries";
import { useOpenTodoCount } from "@todos/openTodos";
import { isUrgentTodo, TodoRow } from "@todos/TodoRow";
import type { ReactNode } from "react";

const Header = ({ meta }: { meta?: ReactNode }) => (
  <header className="flex shrink-0 items-baseline justify-between gap-4 border-b-[1.5px] border-ink pb-3">
    <h1 className="font-display text-[1.625rem] font-medium tracking-[-0.01em] text-ink">
      Your to-dos
    </h1>
    {meta && (
      <span className="text-mono-time text-[0.8125rem] text-annotation">
        {meta}
      </span>
    )}
  </header>
);

const TodoList = () => {
  const sessionKey = useSessionKey();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useOpenTasks(sessionKey);
  // Same cache entry as the nav badge, so the two counts agree.
  const openCount = useOpenTodoCount(sessionKey);
  const todos = data.pages.flatMap((page) => page.tasks);
  const urgent = todos.filter(isUrgentTodo).length;
  const open = Math.max(openCount, todos.length);

  return (
    <>
      <Header
        meta={[`${open} open`, urgent > 0 && `${urgent} urgent`]
          .filter(Boolean)
          .join(" · ")}
      />
      {todos.length === 0 ? (
        <EmptyNote>Nothing to do.</EmptyNote>
      ) : (
        <ul className="flex flex-col gap-1 pt-3">
          {todos.map((task) => (
            <TodoRow key={task.id} task={task} />
          ))}
        </ul>
      )}
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-3 self-start"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          Show more
        </Button>
      )}
    </>
  );
};

export const TodosPage = () => (
  <section
    aria-label="Your to-dos"
    className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-[4px] border border-rule bg-card px-9 pt-[30px] pb-6 shadow-sheet"
  >
    <AsyncBoundary
      skeleton={
        <>
          <Header />
          <SkeletonStack count={4} itemClassName="h-12" className="mt-3" />
        </>
      }
      fallback={(retry) => (
        <>
          <Header />
          <PanelError label="your to-dos" onRetry={retry} />
        </>
      )}
    >
      <TodoList />
    </AsyncBoundary>
  </section>
);
